/** @jsxImportSource @emotion/react */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Form, Input, InputNumber, Modal, Space, Spin, Switch, Table, Tag } from 'antd';
import { SettingOutlined, PlusOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { currentLocale } from '../../i18n';
import { useSelector } from 'react-redux';
import { css } from '@emotion/react';
import { commonStyles } from '../../styles/commonStyles';
import MainLayout from '../../components/common/MainLayout';
import PlanEditor from '../../components/grid/PlanEditor';
import { useGetFieldPlanQuery, usePatchFieldPlanMutation } from '../../api/planApi';
import type { FieldPlan, PlanCellKind, PlanOperation } from '../../types/plan';
import { toastError, toastSuccess } from '../../utils/toast';
import { useGetFieldByIdQuery, useUpdateFieldMutation, useDeleteFieldMutation } from '../../api/fieldsApi';
import { useGetSaveRuntimeSessionQuery } from '../../api/simulationApi';
import { hasAction } from '../../types/permissions';
import { useSave } from '../../contexts/WorldContext';
import ActionWizard from '../../components/events/ActionWizard';
import type { ActionTarget } from '../../components/events/ActionWizard.types';
import { selectIsAuthenticated } from '../../store/slices/authSlice';

const styles = {
  fieldSubtitle: css({ marginTop: 8, marginBottom: 16, color: '#666', fontSize: '14px' }),
  plotsSection: css({
    marginTop: 16,
  }),
  plotItem: css({
    cursor: 'pointer',
    transition: 'background-color 0.2s',
    '&:hover': {
      backgroundColor: '#f0f0f0',
    },
  }),
  plotDetails: css({
    marginTop: 8,
    color: '#666',
    fontSize: 14,
  }),
  plotSpacing: css({
    marginRight: 16,
  }),
  emptyStateCard: css({
    textAlign: 'center' as const,
    padding: 24,
    color: '#999',
  }),
  emptyFieldContainer: css({
    display: 'flex',
    flexDirection: 'column' as const,
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
    padding: '48px',
    backgroundColor: '#fafafa',
    borderRadius: '8px',
    border: '2px dashed #d9d9d9',
  }),
  emptyFieldIcon: css({
    fontSize: '64px',
    color: '#bfbfbf',
    marginBottom: '24px',
  }),
  emptyFieldText: css({
    fontSize: '18px',
    color: '#8c8c8c',
    marginBottom: '24px',
  }),
};

const FieldPlanEditor: React.FC = () => {
  const { saveId, fieldId } = useParams<{ saveId: string; fieldId: string }>();
  const { t } = useTranslation('planning');
  const save = useSave();
  const farmId = save.farmId;
  const navigate = useNavigate();
  const isAuthenticated = useSelector(selectIsAuthenticated);
  const user = useSelector((state: any) => state.auth.user);
  const [showTableView, setShowTableView] = useState(false);
  const [createPlotModalVisible, setCreatePlotModalVisible] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [newPlotName, setNewPlotName] = useState('');
  const [selectedCell, setSelectedCell] = useState<{ row: number; col: number } | null>(null);
  const createPlotInFlightRef = useRef(false);
  const [actionWizardOpen, setActionWizardOpen] = useState(false);
  const [actionTargets, setActionTargets] = useState<ActionTarget[]>([]);
  const [form] = Form.useForm();
  const [editForm] = Form.useForm();
  const [modal, contextHolder] = Modal.useModal();
  const [updateField, { isLoading: updating }] = useUpdateFieldMutation();
  const [deleteField] = useDeleteFieldMutation();

  // CN-0012: farmId может быть либо в URL, либо загружается из field.farmId
  const { data: field, isLoading: isFieldLoading } = useGetFieldByIdQuery(
    { saveId: saveId ?? '', fieldId: fieldId || '' },
    { skip: !saveId || !fieldId }
  );
  
  const actualFarmId = farmId || field?.farmId?.toString();

  const { data: activeSimSession } = useGetSaveRuntimeSessionQuery(saveId ?? '', {
    skip: !saveId || !isAuthenticated,
  });
  
  const actions = field?.actions || [];
  const canEditPlan = hasAction(user, 'fields', 'fields.update', actualFarmId);
  const canUpdateField = actions.some((a: any) => a.code === 'fields.update');
  const canDeleteField = actions.some((a: any) => a.code === 'fields.delete');

  const {
    data: planData,
    isLoading,
    refetch,
  } = useGetFieldPlanQuery(
    { saveId: saveId ?? '', fieldId: fieldId || '' },
    { skip: !saveId || !fieldId }
  );
  const [patchPlan, { isLoading: isPatching }] = usePatchFieldPlanMutation();

  const [localPlan, setLocalPlan] = useState<FieldPlan | null>(null);

  useEffect(() => {
    if (!planData) return;
    setLocalPlan((prev) => {
      if (!prev) return planData;
      // Same race as in PlotPlanEditor: PATCH /plan response includes the
      // freshly created plot / structural object, but the SaveV2 world-
      // snapshot refetch can return the pre-mutation view. FieldPlan has no
      // explicit `plots[]` array — plots are derived from cells[].plotId.
      const apiPlotIds = new Set<string>();
      (planData.cells || []).forEach((c) => { if (c.plotId) apiPlotIds.add(String(c.plotId)); });
      const prevPlotIds: string[] = [];
      const seen = new Set<string>();
      (prev.cells || []).forEach((c) => {
        if (c.plotId && !seen.has(String(c.plotId))) {
          seen.add(String(c.plotId));
          prevPlotIds.push(String(c.plotId));
        }
      });
      for (const id of prevPlotIds) {
        if (!apiPlotIds.has(id)) return prev;
      }
      const apiSoIds = new Set((planData.structuralObjects || []).map((s) => s.id));
      for (const so of prev.structuralObjects || []) {
        if (!apiSoIds.has(so.id)) return prev;
      }
      return planData;
    });
  }, [planData]);

  // Extract plots from cells for table view
  const plots = useMemo(() => {
    if (!localPlan?.cells) return [];

    const namesById = new Map<string, string>();
    (field?.plots || []).forEach((p: any) => {
      if (p?.id) namesById.set(String(p.id), p.name || t('fieldEditor.plotWithId', { id: String(p.id).slice(0, 8) }));
    });

    const bedsByPlot = new Map<string, number>();
    localPlan.cells.forEach((cell) => {
      if (cell.kind === 'bed' && cell.plotId) {
        bedsByPlot.set(cell.plotId, (bedsByPlot.get(cell.plotId) || 0) + 1);
      }
    });

    const plotMap = new Map<string, { id: string; name: string; bedsCount: number }>();
    localPlan.cells.forEach((cell) => {
      if (cell.kind !== 'plot' || !cell.plotId) return;
      if (!plotMap.has(cell.plotId)) {
        plotMap.set(cell.plotId, {
          id: cell.plotId,
          name: namesById.get(cell.plotId) || t('fieldEditor.plotWithId', { id: cell.plotId.slice(0, 8) }),
          bedsCount: bedsByPlot.get(cell.plotId) || 0,
        });
      }
    });

    return Array.from(plotMap.values()).sort((a, b) => a.name.localeCompare(b.name, currentLocale()));
  }, [localPlan, field?.plots, t]);

  // Debug logging
  console.log('[FieldPlanEditor] Render state:', {
    isLoading,
    isFieldLoading,
    hasLocalPlan: !!localPlan,
    hasPlanData: !!planData,
    actualFarmId,
    fieldId,
    field: field?.id,
    planDataCells: planData?.cells?.length
  });

  const handlePatch = async (operations: PlanOperation[]) => {
    if (!fieldId || !field?.id) return;
    try {
      const updated = await patchPlan(
        { saveId: saveId || '', fieldId: String(field.id), operations }
      ).unwrap();
      setLocalPlan(updated);
    } catch (error: any) {
      const status = error?.status ?? error?.originalStatus;
      if (status === 403) {
        toastError(t('fieldEditor.planLimit'));
        return;
      }
      toastError(error);
    }
  };

  // CN-0014: Click on empty cell opens modal to create plot
  const handleEmptyCellClick = (row: number, col: number) => {
    if (!canEditPlan) return;
    
    // Backend will automatically assign (1,1) for empty field
    setSelectedCell({ row, col });
    setNewPlotName('');
    form.resetFields();
    setCreatePlotModalVisible(true);
  };

  const handleCreatePlotConfirm = async () => {
    if (!fieldId || !field?.id || !selectedCell || createPlotInFlightRef.current || isPatching) return;

    createPlotInFlightRef.current = true;
    const requestedCell = selectedCell;
    const previousPlotIds = new Set(
      (localPlan?.cells ?? planData?.cells ?? [])
        .map((cell) => cell.plotId)
        .filter((plotId): plotId is string => Boolean(plotId))
    );

    try {
      await form.validateFields();
      const plotName = newPlotName.trim() || t('fieldEditor.defaultPlotName', { time: new Date().toLocaleTimeString(currentLocale()) });

      // Create plot with single cell (plots are 1x1)
      const updated = await patchPlan(
        { saveId: saveId || '', fieldId: String(field.id), operations: [{
          op: 'createPlot',
          cells: [requestedCell],
          plot: { name: plotName, area: 1 },
        }] }
      ).unwrap();

      setLocalPlan(updated);
      toastSuccess(t('fieldEditor.toasts.plotCreated'));
      setCreatePlotModalVisible(false);
      
      const newPlot = updated.cells.find((cell) => cell.plotId && !previousPlotIds.has(cell.plotId))
        || updated.cells.find((cell) => (
          cell.row === requestedCell.row
          && cell.col === requestedCell.col
          && cell.plotId
        ));
      if (newPlot?.plotId) {
        navigate(`/saves/${saveId}/plots/${newPlot.plotId}/plan`);
      }
      await refetch();
    } catch (error: any) {
      if (error?.errorFields) {
        // Form validation errors
        return;
      }

      const status = error?.status ?? error?.originalStatus ?? error?.data?.status;
      if (status === 409) {
        const refreshed = await refetch();
        const refreshedPlan = refreshed?.data as FieldPlan | undefined;
        const existingPlotCell = refreshedPlan?.cells?.find((cell) => (
          cell.row === requestedCell.row
          && cell.col === requestedCell.col
          && cell.plotId
        ));

        if (refreshedPlan) {
          setLocalPlan(refreshedPlan);
        }

        if (existingPlotCell?.plotId) {
          setCreatePlotModalVisible(false);
          toastSuccess(t('fieldEditor.toasts.plotAlreadyCreated'));
          navigate(`/saves/${saveId}/plots/${existingPlotCell.plotId}/plan`);
          return;
        }
      }

      toastError(error);
    } finally {
      createPlotInFlightRef.current = false;
    }
  };

  const handleOpenSettings = () => {
    if (!field) return;
    editForm.setFieldsValue({
      name: field.name,
      area: field.area ?? undefined,
      location_coords: field.location_coords ?? undefined,
      description: field.description ?? undefined,
    });
    setEditOpen(true);
  };

  const handleSaveEdit = async () => {
    if (!field || !fieldId) return;
    try {
      const values = await editForm.validateFields();
      await updateField({ saveId: saveId || '', id: String(field.id), payload: values }).unwrap();
      setEditOpen(false);
      refetch();
      toastSuccess(t('fieldEditor.toasts.fieldUpdated'));
    } catch (error) {
      toastError(error);
    }
  };

  const handleDeleteFromModal = () => {
    if (!field || !fieldId) return;
    setEditOpen(false);
    modal.confirm({
      title: t('fieldPage.deleteFieldConfirm'),
      content: t('fieldEditor.deleteFieldContent'),
      okText: t('common:actions.delete'),
      okType: 'danger',
      cancelText: t('common:actions.cancel'),
      onOk: async () => {
        try {
          await deleteField({ saveId: saveId || '', id: String(field.id) }).unwrap();
          toastSuccess(t('fieldEditor.toasts.fieldDeleted'));
          navigate(actualFarmId ? `/saves/${saveId}` : '/');
        } catch (error) {
          toastError(error);
        }
      },
    });
  };

  const handlePlotClick = (plotId: string | number) => {
    // CN-0012: Navigate using UUID-only route
    navigate(`/saves/${saveId}/plots/${plotId}/plan`);
  };

  const handleFieldAction = () => {
    if (!fieldId) return;
    setActionTargets([{ fieldId: String(fieldId), label: fieldName }]);
    setActionWizardOpen(true);
  };

  const handlePlotAction = (plotId: string, plotName: string) => {
    setActionTargets([{ plotId, label: plotName }]);
    setActionWizardOpen(true);
  };

  const paintKinds = useMemo<PlanCellKind[]>(() => [], []);

  if (!fieldId) {
    return (
      <MainLayout>
        <Card>{t('fieldEditor.noFieldId')}</Card>
      </MainLayout>
    );
  }

  const fieldName = field?.name || fieldId;
  const fieldArea = field?.area ? ` (${t('fieldEditor.areaHa', { area: field.area })})` : '';
  const isFieldEmpty = !localPlan?.cells || localPlan.cells.length === 0;

  return (
    <MainLayout>
      {contextHolder}
      <h2>{t('fieldPage.fieldTitle', { name: fieldName })}{fieldArea}</h2>
      {field?.description && <div css={styles.fieldSubtitle}>{field.description}</div>}
      <Space direction="vertical" size={16} style={{ width: '100%' }}>
        <Card>
          <Space wrap>
            <Button onClick={() => navigate(-1)}>{t('common:actions.back')}</Button>
            {(canUpdateField || canDeleteField) && (
              <Button
                icon={<SettingOutlined />}
                onClick={handleOpenSettings}
              >
                {t('fieldEditor.settings')}
              </Button>
            )}
            <Button type="primary" onClick={handleFieldAction}>
              {t('fieldEditor.fieldAction')}
            </Button>
            <div style={{ marginLeft: 16, display: 'flex', alignItems: 'center', gap: 8 }}>
              <span>{t('fieldEditor.tableView')}</span>
              <Switch checked={showTableView} onChange={setShowTableView} />
            </div>
          </Space>
        </Card>

        {!showTableView && (
          <>
            {(isLoading && actualFarmId) || isFieldLoading || !actualFarmId ? (
              <Card>
                <Spin />
              </Card>
            ) : !planData ? (
              <Card>
                <div>{t('fieldEditor.loadFailed')}</div>
              </Card>
            ) : isFieldEmpty && canEditPlan ? (
              <Card>
                <div css={styles.emptyFieldContainer}>
                  <PlusOutlined css={styles.emptyFieldIcon} />
                  <div css={styles.emptyFieldText}>{t('fieldEditor.emptyField')}</div>
                  <Button
                    type="primary"
                    size="large"
                    icon={<PlusOutlined />}
                    onClick={() => handleEmptyCellClick(1, 1)}
                  >
                    {t('fieldEditor.createFirstPlot')}
                  </Button>
                </div>
              </Card>
            ) : (
              <PlanEditor
                boundingBox={localPlan?.boundingBox || planData.boundingBox}
                allowedBoundingBox={localPlan?.allowedBoundingBox || planData.allowedBoundingBox}
                globalGrid={localPlan?.globalGrid || planData.globalGrid}
                cells={localPlan?.cells || planData.cells}
                structuralObjects={localPlan?.structuralObjects || planData.structuralObjects}
                beds={localPlan?.beds || planData.beds}
                paintKinds={paintKinds}
                saveId={saveId || ''}
                farmId={actualFarmId}
                disablePlantingMode
                isBusy={isPatching}
                onPatch={handlePatch}
                onPlotClick={handlePlotClick}
                onManagementPlotAction={(plotIds) => {
                  const plotId = plotIds[0];
                  if (!plotId) return;
                  const name = plots.find((p) => p.id === plotId)?.name || t('fieldEditor.plotWithId', { id: plotId.slice(0, 8) });
                  handlePlotAction(plotId, name);
                }}
                onEmptyCellClick={canEditPlan ? handleEmptyCellClick : undefined}
              />
            )}
          </>
        )}

        {showTableView && plots.length > 0 && (
          <div css={styles.plotsSection}>
            <h3>{t('fieldPage.plots')}</h3>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={plots}
              onRow={(record: any) => ({
                onClick: () => navigate(`/saves/${saveId}/plots/${record.id}/plan`),
                style: { cursor: 'pointer' },
              })}
              columns={[
                {
                  title: t('fieldEditor.columns.plot'),
                  dataIndex: 'name',
                  key: 'name',
                  width: '55%',
                  render: (name: string) => <strong>{name}</strong>,
                },
                {
                  title: t('fieldEditor.columns.beds'),
                  dataIndex: 'bedsCount',
                  key: 'bedsCount',
                  width: 140,
                  render: (count: number) => (count > 0 ? <Tag color="blue">{count}</Tag> : <span style={{ color: '#8c8c8c' }}>0</span>),
                },
                {
                  title: t('fieldEditor.columns.action'),
                  key: 'action',
                  width: 180,
                  render: (_: unknown, record: any) => (
                    <Button
                      size="small"
                      onClick={(e) => {
                        e.stopPropagation();
                        handlePlotAction(record.id, record.name);
                      }}
                    >
                      {t('fieldEditor.assignOrRun')}
                    </Button>
                  ),
                },
              ]}
            />
          </div>
        )}

        {showTableView && plots.length === 0 && (
          <Card css={styles.emptyStateCard}>
            <div css={styles.emptyFieldContainer}>
              <PlusOutlined css={styles.emptyFieldIcon} />
              <div css={styles.emptyFieldText}>{t('fieldEditor.noPlots')}</div>
              {canEditPlan && (
                <Button
                  type="primary"
                  size="large"
                  icon={<PlusOutlined />}
                  onClick={() => handleEmptyCellClick(1, 1)}
                >
                  {t('fieldEditor.createFirstPlot')}
                </Button>
              )}
            </div>
          </Card>
        )}
      </Space>

      {/* Modal for field settings */}
      <Modal
        title={t('fieldEditor.settingsTitle')}
        open={editOpen}
        onCancel={() => setEditOpen(false)}
        footer={[
          <Button key="cancel" onClick={() => setEditOpen(false)}>
            {t('common:actions.cancel')}
          </Button>,
          canDeleteField && (
            <Button key="delete" danger onClick={handleDeleteFromModal}>
              {t('fieldEditor.deleteField')}
            </Button>
          ),
          <Button key="save" type="primary" loading={updating} onClick={handleSaveEdit}>
            {t('common:actions.save')}
          </Button>,
        ]}
      >
        <Form form={editForm} layout="vertical">
          <Form.Item label={t('fieldPage.form.name')} name="name" rules={[{ required: true, message: t('fieldPage.form.nameRequired') }]}>
            <Input />
          </Form.Item>
          <Form.Item label={t('fieldPage.form.areaHa')} name="area">
            <InputNumber css={commonStyles.fullWidth} />
          </Form.Item>
          <Form.Item label={t('fieldPage.form.coords')} name="location_coords">
            <Input />
          </Form.Item>
          <Form.Item label={t('common:labels.description')} name="description">
            <Input.TextArea rows={3} />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal for creating new plot */}
      <Modal
        title={t('fieldEditor.createPlotTitle')}
        open={createPlotModalVisible}
        onOk={handleCreatePlotConfirm}
        onCancel={() => setCreatePlotModalVisible(false)}
        okText={t('common:actions.create')}
        cancelText={t('common:actions.cancel')}
        confirmLoading={isPatching}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            label={t('fieldEditor.plotName')}
            name="plotName"
            rules={[
              { required: true, message: t('fieldEditor.plotNameRequired') },
              { max: 100, message: t('fieldEditor.maxLength', { max: 100 }) },
            ]}
          >
            <Input
              placeholder={t('fieldEditor.plotNamePlaceholder')}
              value={newPlotName}
              onChange={e => setNewPlotName(e.target.value)}
              autoFocus
            />
          </Form.Item>
        </Form>
      </Modal>

      {actualFarmId && (
        <ActionWizard
          open={actionWizardOpen}
          onClose={() => {
            setActionWizardOpen(false);
            setActionTargets([]);
          }}
          targets={actionTargets}
          farmId={actualFarmId}
          onSuccess={() => {
            setActionWizardOpen(false);
            setActionTargets([]);
            refetch();
          }}
          contextLabel={actionTargets[0]?.label || t('fieldPage.fieldTitle', { name: fieldName })}
          simSessionId={activeSimSession?.id}
        />
      )}
    </MainLayout>
  );
};

export default FieldPlanEditor;
